import { RouteParams } from '@/routes';
import { useCandidates, useUser } from '@/state';
import { CandidateAndReview, useCandidate } from '@/state/use-candidate';
import { useComments } from '@/state/use-comments';
import { useRatings } from '@/state/use-ratings';
import { UserClaims } from '@okta/okta-auth-js';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { useScreenSize } from '@repo/ui/hooks';
import { Review } from '@repo/ui/models/candidate';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { Candidate } from './candidate';
import { ReplyPayload } from './candidate.scores';
import { SelfCommentModel } from './self-comment';

dayjs.extend(relativeTime);

export function ResponsiveCandidateWrapper() {
  const { isSmall, isMedium } = useScreenSize();
  const { candidateId: submissionID } = useParams<RouteParams>();
  const {
    createComment,
    loading,
    error,
    fetchComments,
    data: comments,
    updateComment,
    replyComment,
  } = useComments();
  const {
    createRating,
    ratingAverage,
    loading: ratingLoading,
    fetchCandidateRatingAverage,
  } = useRatings();
  const { user } = useUser();
  const { updateCandidateRating } = useCandidates();
  const [userComment, setUserComment] = useState<
    SelfCommentModel | undefined
  >();
  const [inEditMode, setInEditMode] = useState(false);
  const candidate = useCandidate(String(submissionID));
  const isProcessing = loading || ratingLoading;
  const existingRate = candidate?.ratings?.find(
    (x) => x.authorID === user?.sub,
  );

  const handleActionClick = async (rating: number, comment?: string) => {
    if (!submissionID) {
      showErrorToast({ title: 'Unable to add rating' });
      return;
    }

    try {
      if (comment) {
        await createComment({
          input: {
            body: comment,
            submissionID,
          },
          user: user || ({} as UserClaims),
        });
      }
      await createRating({
        input: {
          rating,
          submissionID,
        },
      });
      updateCandidateRating(submissionID, rating, user?.sub ?? '');
    } catch (error) {
      showErrorToast();
      console.error('Failed to add rating/comment:', error);
    }
  };

  const handleOnCommentEditClick = (review: Review) => {
    setUserComment((prev) => {
      if (!prev) return undefined;
      return {
        ...prev,
        comment: review.comment,
        formattedDate: `${dayjs(Date.now()).fromNow(true)} ago`,
      };
    });
    setInEditMode(true);
  };

  const handleUpdateClick = async (commentId: string, comment: string) => {
    if (!submissionID) {
      toast.error('Candidate ID is missing');
      return;
    }
    if (!commentId) {
      toast.error('Comment ID is missing');
      return;
    }
    setUserComment((prev) => {
      if (!prev) return undefined;
      return {
        ...prev,
        comment,
        formattedDate: `${dayjs(Date.now()).fromNow(true)} ago`,
      };
    });
    setInEditMode(false);
    await updateComment(commentId, comment);
  };

  const handleReplyClick = async (data: ReplyPayload) => {
    if (!submissionID) {
      toast.error('Candidate ID is missing');
      return;
    }
    if (!data.comment) {
      toast.error('Comment should not be empty');
      return;
    }
    try {
      await replyComment(
        data.commentId,
        data.reviewId,
        data.comment,
        submissionID,
      );
    } catch (error) {
      showErrorToast({ title: 'Unable to add reply' });
      console.error('Failed to add comment reply:', error);
    }
  };

  useEffect(() => {
    if (error) {
      showErrorToast({ description: error });
    }
  }, [error]);

  useEffect(() => {
    if (submissionID) {
      fetchComments({ submissionID, showPrimaryComments: true });
      fetchCandidateRatingAverage({ submissionID });
    }
  }, [submissionID]);

  useEffect(() => {
    const userComment = comments
      .map((x) => ({
        ...x,
        formattedDate: `${dayjs(x.createdAt).fromNow(true)} ago`,
      }))
      .find((x) => x.jury === user?.name && !x.parentID);
    setUserComment(userComment);
  }, [JSON.stringify(comments)]);

  //TODO Improve copy and styling
  if (!candidate) {
    return <div>The candidate does not exist</div>;
  }

  // <Candidate /> Occupies the entire screen on small screens
  if (isSmall || isMedium) {
    return (
      <div className="fixed inset-0 bg-zinc-50 z-50 overflow-scroll">
        <Candidate
          key={candidate._id.toString()}
          candidate={candidate as CandidateAndReview}
          onCommentAddClick={handleActionClick}
          onSkipClick={handleActionClick}
          onUpdateClick={handleUpdateClick}
          processing={isProcessing}
          rating={candidate?.rating?.rating || existingRate?.rating}
          ratingAverage={ratingAverage}
          userName={user?.name ?? ''}
          userComment={userComment}
          inEditMode={inEditMode}
          onCommentEditClick={handleOnCommentEditClick}
          onCommentReplyClick={handleReplyClick}
        />
      </div>
    );
  } else {
    return (
      <Candidate
        key={candidate._id.toString()}
        candidate={candidate as CandidateAndReview}
        onCommentAddClick={handleActionClick}
        onSkipClick={handleActionClick}
        onUpdateClick={handleUpdateClick}
        processing={isProcessing}
        rating={existingRate?.rating}
        ratingAverage={ratingAverage}
        userName={user?.name ?? ''}
        userComment={userComment}
        inEditMode={inEditMode}
        onCommentEditClick={handleOnCommentEditClick}
        onCommentReplyClick={handleReplyClick}
      />
    );
  }
}
